import type { DetailedReport } from '../api/types';

export interface DailySalesPoint {
  date: string;
  label: string;
  sales: number;
  tickets: number;
}

export interface PaymentMethodPoint {
  method: string;
  amount: number;
}

const dateLabelFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: 'short',
});

export function aggregateSalesByDay(reports: DetailedReport[]): DailySalesPoint[] {
  const byDate = new Map<string, { sales: number; tickets: number }>();

  for (const report of reports) {
    const date = report.date.slice(0, 10);
    const current = byDate.get(date) ?? { sales: 0, tickets: 0 };
    current.sales += Number(report.total);
    current.tickets += Number(report.items ?? 0);
    byDate.set(date, current);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, values]) => ({
      date,
      label: dateLabelFormatter.format(new Date(`${date}T12:00:00`)),
      sales: values.sales,
      tickets: values.tickets,
    }));
}

export function aggregatePaymentMethods(reports: DetailedReport[]): PaymentMethodPoint[] {
  const totals = new Map<string, number>();

  for (const report of reports) {
    if (!report.revenue_breakdown) continue;
    for (const [method, amount] of Object.entries(report.revenue_breakdown)) {
      const label = paymentMethodLabel(method);
      totals.set(label, (totals.get(label) ?? 0) + Number(amount));
    }
  }

  return [...totals.entries()]
    .map(([method, amount]) => ({ method, amount }))
    .sort((a, b) => b.amount - a.amount);
}

function paymentMethodLabel(method: string): string {
  switch (method.trim().toUpperCase()) {
    case 'EFECTIVO':
    case 'CASH':
      return 'Efectivo';
    case 'TARJETA':
      return 'Tarjeta';
    case 'TERMINAL_STRIPE':
      return 'Terminal Stripe';
    case 'AUTOSERVICIO':
      return 'Autoservicio';
    default:
      return method;
  }
}

export const CHART_COLORS = [
  '#e8512f',
  '#f97316',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#64748b',
];
